'use client';

import React, { useState, useTransition } from 'react';
import Link from 'next/link';
import { Eye, EyeOff } from 'lucide-react';
import { barberLoginAction, barberForgotPasswordAction } from './actions';

const GOLD = '#C9A84C';
const GOLD_RGB = '201,168,76';
const NAVY = '#0a0e1a';
const PAGE_BG = '#080c18';
const CARD_BG = 'rgba(255,255,255,0.04)';

export default function BarberLoginForm({ initialError }: { initialError?: string }) {
  const [error, setError] = useState(initialError || '');
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPending, startForgotTransition] = useTransition();
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    border: `1.5px solid rgba(${GOLD_RGB},0.2)`,
    color: '#f0e6c8',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = GOLD;
    e.target.style.boxShadow = `0 0 0 3px rgba(${GOLD_RGB},0.12)`;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = `rgba(${GOLD_RGB},0.2)`;
    e.target.style.boxShadow = 'none';
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const formData = new FormData(e.currentTarget);
    startTransition(async () => {
      const result = await barberLoginAction(formData);
      if (result?.error) setError(result.error);
    });
  };

  const handleForgotSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    const formData = new FormData(e.currentTarget);
    formData.set('origin', window.location.origin);
    startForgotTransition(async () => {
      const result = await barberForgotPasswordAction(formData);
      if (result?.error) {
        setForgotError(result.error);
      } else {
        setForgotMessage('Check your email — we sent a password reset link.');
      }
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: PAGE_BG, fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Ambient gold glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 60% 40% at 50% 0%, rgba(${GOLD_RGB},0.07) 0%, transparent 70%)`,
        }}
      />

      <div
        className="relative w-full max-w-md rounded-3xl p-8 md:p-10"
        style={{
          background: CARD_BG,
          border: `1px solid rgba(${GOLD_RGB},0.2)`,
          boxShadow: `0 8px 48px rgba(${GOLD_RGB},0.08), 0 2px 12px rgba(0,0,0,0.3)`,
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{
              background: `rgba(${GOLD_RGB},0.12)`,
              border: `1px solid rgba(${GOLD_RGB},0.3)`,
              boxShadow: `0 0 24px rgba(${GOLD_RGB},0.15)`,
            }}
          >
            <span className="font-black text-2xl" style={{ color: GOLD }}>✦</span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <div className="h-px w-8" style={{ background: `linear-gradient(to right, transparent, ${GOLD})` }} />
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
              The Barber Sphere
            </span>
            <div className="h-px w-8" style={{ background: `linear-gradient(to left, transparent, ${GOLD})` }} />
          </div>

          <h1 className="text-2xl font-black tracking-tight text-center" style={{ color: '#f0e6c8' }}>
            {showForgot ? 'Reset Password' : 'Barber Login'}
          </h1>
          <p className="text-sm mt-1 text-center" style={{ color: `rgba(${GOLD_RGB},0.6)` }}>
            {showForgot ? 'Enter your email to receive a reset link' : 'Access your barber dashboard'}
          </p>
        </div>

        {/* Forgot Password Form */}
        {showForgot ? (
          <div>
            {forgotMessage ? (
              <div
                className="px-4 py-3 rounded-xl text-sm mb-4"
                style={{
                  background: 'rgba(52,211,153,0.08)',
                  border: '1px solid rgba(52,211,153,0.25)',
                  color: '#34d399',
                }}
              >
                {forgotMessage}
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: `rgba(${GOLD_RGB},0.7)` }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@barbershop.com"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                {forgotError && (
                  <div
                    className="px-4 py-3 rounded-xl text-sm"
                    style={{
                      background: 'rgba(239,68,68,0.07)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      color: '#f87171',
                    }}
                  >
                    {forgotError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotPending}
                  className="w-full py-3.5 rounded-2xl font-black text-sm transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{
                    background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
                    color: NAVY,
                    boxShadow: `0 4px 24px rgba(${GOLD_RGB},0.3)`,
                  }}
                >
                  {forgotPending ? 'Sending…' : 'Send Reset Link →'}
                </button>
              </form>
            )}

            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(false); setForgotMessage(''); setForgotError(''); }}
                className="text-xs font-semibold transition-opacity hover:opacity-80"
                style={{ color: GOLD }}
              >
                ← Back to Sign In
              </button>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: `rgba(${GOLD_RGB},0.7)` }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@barbershop.com"
                  autoComplete="email"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>

              <div className="space-y-1.5">
                <label
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: `rgba(${GOLD_RGB},0.7)` }}
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ ...inputStyle, paddingRight: '44px' }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80"
                    style={{ color: GOLD }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs font-semibold transition-opacity hover:opacity-80"
                    style={{ color: GOLD }}
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: 'rgba(239,68,68,0.07)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171',
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isPending}
                className="w-full py-3.5 rounded-2xl font-black text-sm transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
                  color: NAVY,
                  boxShadow: `0 4px 24px rgba(${GOLD_RGB},0.3)`,
                }}
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: 'rgba(26,26,46,0.4) transparent transparent transparent' }}
                    />
                    Signing In…
                  </span>
                ) : (
                  'Sign In →'
                )}
              </button>
            </form>

            <div className="mt-6 flex flex-col items-center gap-2">
              <p className="text-xs" style={{ color: `rgba(${GOLD_RGB},0.5)` }}>
                Don't have an account?{' '}
                <Link
                  href="/barber-signup"
                  className="font-semibold transition-opacity hover:opacity-80"
                  style={{ color: GOLD }}
                >
                  Sign up free
                </Link>
              </p>
              <Link
                href="/"
                className="text-xs transition-opacity hover:opacity-80"
                style={{ color: `rgba(${GOLD_RGB},0.4)` }}
              >
                ← Back to home
              </Link>
            </div>
          </>
        )}

        <p className="text-center text-xs mt-6" style={{ color: `rgba(${GOLD_RGB},0.25)` }}>
          BarberOS · Secure barber portal
        </p>
      </div>
    </div>
  );
}
